import { ref } from 'vue'
import { collection, query, where, getDocs, doc, updateDoc, addDoc, deleteDoc, orderBy } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { TIME_SLOTS, TIME_SLOT_GROUPS, getSlotsByPeriod } from '@/utils/timeSlots'

// 로컬스토리지 키
const POPUP_HIDE_KEY = 'popup_ad_hide_until'

export function usePopupAd() {
  const loading = ref(false)
  const error = ref(null)

  // 현재 시간대 가져오기
  const getCurrentTimeSlot = () => {
    const now = new Date()
    const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))
    const hours = koreaTime.getHours()
    const minutes = koreaTime.getMinutes()
    const currentTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`

    return TIME_SLOTS.find(slot => {
      return currentTime >= slot.start && currentTime < slot.end
    }) || TIME_SLOTS[0]
  }

  // 오늘 보지 않기 체크
  const isHiddenToday = () => {
    const hideUntil = localStorage.getItem(POPUP_HIDE_KEY)
    if (!hideUntil) return false

    const now = new Date()
    const hideDate = new Date(hideUntil)
    return now < hideDate
  }

  // 오늘 보지 않기 설정
  const hideForToday = () => {
    const now = new Date()
    const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))

    // 다음날 00:00로 설정
    koreaTime.setDate(koreaTime.getDate() + 1)
    koreaTime.setHours(0, 0, 0, 0)

    localStorage.setItem(POPUP_HIDE_KEY, koreaTime.toISOString())
  }

  // 현재 노출 가능한 팝업 광고 가져오기
  const getActivePopupAd = async () => {
    // 오늘 보지 않기 체크
    if (isHiddenToday()) {
      return null
    }

    try {
      loading.value = true
      const currentSlot = getCurrentTimeSlot()

      // 승인된 팝업 광고 조회
      const q = query(
        collection(db, 'popupAds'),
        where('status', '==', 'approved'),
        where('timeSlot.id', '==', currentSlot.id)
      )

      const snapshot = await getDocs(q)

      if (snapshot.empty) {
        return null
      }

      // 랜덤으로 하나 선택
      const ads = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      const randomIndex = Math.floor(Math.random() * ads.length)
      return ads[randomIndex]
    } catch (err) {
      console.error('팝업 광고 로드 실패:', err)
      error.value = err.message
      return null
    } finally {
      loading.value = false
    }
  }

  // 노출 수 증가
  const incrementImpressions = async (popupId) => {
    try {
      const popupRef = doc(db, 'popupAds', popupId)
      const snapshot = await getDocs(query(
        collection(db, 'popupAds'),
        where('__name__', '==', popupId)
      ))

      if (!snapshot.empty) {
        const currentData = snapshot.docs[0].data()
        await updateDoc(popupRef, {
          impressions: (currentData.impressions || 0) + 1
        })
      }
    } catch (err) {
      console.error('노출 수 증가 실패:', err)
    }
  }

  // 클릭 수 증가
  const incrementClicks = async (popupId) => {
    try {
      const popupRef = doc(db, 'popupAds', popupId)
      const snapshot = await getDocs(query(
        collection(db, 'popupAds'),
        where('__name__', '==', popupId)
      ))

      if (!snapshot.empty) {
        const currentData = snapshot.docs[0].data()
        await updateDoc(popupRef, {
          clicks: (currentData.clicks || 0) + 1
        })
      }
    } catch (err) {
      console.error('클릭 수 증가 실패:', err)
    }
  }

  // 팝업 광고 신청 (파트너용)
  const applyPopupAd = async (data) => {
    try {
      loading.value = true
      const now = new Date().toISOString()

      const popupData = {
        ...data,
        status: 'pending',
        impressions: 0,
        clicks: 0,
        createdAt: now,
        updatedAt: now
      }

      const docRef = await addDoc(collection(db, 'popupAds'), popupData)
      return docRef.id
    } catch (err) {
      console.error('팝업 광고 신청 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 내 팝업 광고 목록 (파트너용)
  const getMyPopupAds = async (partnerId) => {
    try {
      loading.value = true
      const q = query(
        collection(db, 'popupAds'),
        where('partnerId', '==', partnerId),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('내 팝업 광고 로드 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  // 팝업 광고 취소 (파트너용)
  const cancelPopupAd = async (popupId) => {
    try {
      loading.value = true
      await deleteDoc(doc(db, 'popupAds', popupId))
    } catch (err) {
      console.error('팝업 광고 취소 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 전체 팝업 광고 목록 (어드민용)
  const getAllPopupAds = async () => {
    try {
      loading.value = true
      const q = query(
        collection(db, 'popupAds'),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('팝업 광고 목록 로드 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  // 팝업 광고 상태 변경 (어드민용)
  const updatePopupAdStatus = async (popupId, status, rejectReason = null) => {
    try {
      loading.value = true
      const now = new Date().toISOString()

      const updateData = {
        status,
        updatedAt: now
      }

      if (status === 'approved') {
        updateData.approvedAt = now
      } else if (status === 'rejected' && rejectReason) {
        updateData.rejectReason = rejectReason
        updateData.rejectedAt = now
      }

      await updateDoc(doc(db, 'popupAds', popupId), updateData)
    } catch (err) {
      console.error('상태 변경 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 기존 신청 확인 (파트너용)
  const checkExistingPopupAd = async (partnerId) => {
    try {
      // pending 상태 확인
      const pendingQ = query(
        collection(db, 'popupAds'),
        where('partnerId', '==', partnerId),
        where('status', '==', 'pending')
      )
      const pendingSnapshot = await getDocs(pendingQ)
      if (!pendingSnapshot.empty) return true

      // approved 상태 확인
      const approvedQ = query(
        collection(db, 'popupAds'),
        where('partnerId', '==', partnerId),
        where('status', '==', 'approved')
      )
      const approvedSnapshot = await getDocs(approvedQ)
      return !approvedSnapshot.empty
    } catch (err) {
      console.error('기존 신청 확인 실패:', err)
      return false
    }
  }

  return {
    loading,
    error,
    TIME_SLOTS,
    TIME_SLOT_GROUPS,
    getSlotsByPeriod,
    getCurrentTimeSlot,
    isHiddenToday,
    hideForToday,
    getActivePopupAd,
    incrementImpressions,
    incrementClicks,
    applyPopupAd,
    getMyPopupAds,
    cancelPopupAd,
    getAllPopupAds,
    updatePopupAdStatus,
    checkExistingPopupAd
  }
}
